import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';

import { initialState } from './reducer';
import makeSelectMain, { getIsLoanAvailable, selectMainStatus } from '../Main/selectors';
import {
  GENERIC_FEE_SCHEDULE,
  HAWAII_FEE_SCHEDULE,
  NEW_YORK_FEE_SCHEDULE,
  PATH_TO_FEE_SCHEDULE,
} from './constants';

/**
 * Direct selector to the fees state domain
 */
const selectFeesDomain = (state) => state.fees || initialState;

/**
 * Direct selector to feeSchedule props
 */
const selectFeesProps = (_state, props) => props;

const selectDesktopIsNewYorkFeeSchedule = () => createSelector(
  selectFeesProps,
  ({ location: { pathname } }) => pathname === '/nyfeeschedule',
);

const selectWhichDesktopFeeSchedule = () => createSelector(
  selectFeesProps,
  ({ location: { pathname } }) => PATH_TO_FEE_SCHEDULE[pathname] || 'GENERIC',
);

const selectDesktopPageTitle = () => createSelector(
  selectWhichDesktopFeeSchedule(),
  (feeScheduleType) => {
    let stateName = '';
    switch (feeScheduleType) {
      case 'HAWAII':
        stateName = 'Hawaii ';
        break;
      case 'NEWYORK':
        stateName = 'New York ';
        break;
      default:
        stateName = '';
    }
    return `${stateName}Mortgage Servicing Fee Schedule`;
  },
);

const selectRequestStatus = (prop) => createSelector(
  makeSelectFees('requestStatus'),
  (requestStatus) => requestStatus[prop],
);

const selectHasPDFFeeStatement = () => createSelector(
  makeSelectFees('feeStatement'),
  makeSelectFees('preauthFeeStatements'),
  makeSelectMain('isLoggedIn'),
  getIsLoanAvailable(),
  (feeStatement, preauthFees, isLoggedIn, isLoanAvailable) =>
    isLoggedIn ? (!!feeStatement && isLoanAvailable) : !isEmpty(preauthFees),
);

const getFormattedPreauthFees = () => createSelector(
  makeSelectFees('preauthFeeStatements'),
  (preauthFees) => preauthFees.reduce((acc, { statementStateCode, statementUrl }) => {
    acc[statementStateCode] = statementUrl;
    return acc;
  }, {}),
);

const getFormattedStringFeeSchedule = () => createSelector(
  makeSelectFees('feeSchedule'),
  makeSelectFees('disclaimer'),
  makeSelectFees('asOfDate'),
  selectWhichDesktopFeeSchedule(),
  (
    feeScheduleState,
    disclaimerState,
    asOfDateState,
    feeScheduleType,
  ) => {
    let feeScheduleToUse = GENERIC_FEE_SCHEDULE;
    switch (feeScheduleType) {
      case 'HAWAII':
        feeScheduleToUse = HAWAII_FEE_SCHEDULE;
        break;
      case 'NEWYORK':
        feeScheduleToUse = NEW_YORK_FEE_SCHEDULE;
        break;
      default:
        feeScheduleToUse = GENERIC_FEE_SCHEDULE;
    }
    const { asOfDate, disclaimer, feeSchedule } = feeScheduleToUse;
    return ({
      asOfDate: asOfDateState || asOfDate,
      disclaimer: !isEmpty(disclaimerState) ? disclaimerState : disclaimer,
      feeSchedule: !isEmpty(feeScheduleState) ? feeScheduleState : feeSchedule,
    });
  },
);

const getFormattedMobileStringFeeSchedule = () => createSelector(
  makeSelectFees('feeSchedule'),
  makeSelectFees('disclaimer'),
  makeSelectFees('asOfDate'),
  (
    feeScheduleState,
    disclaimerState,
    asOfDateState,
  ) => {
    const { asOfDate, disclaimer, feeSchedule } = GENERIC_FEE_SCHEDULE;
    return ({
      GENERIC: GENERIC_FEE_SCHEDULE,
      HAWAII: HAWAII_FEE_SCHEDULE,
      LOGGEDIN: {
        asOfDate: asOfDateState || asOfDate,
        disclaimer: !isEmpty(disclaimerState) ? disclaimerState : disclaimer,
        feeSchedule: !isEmpty(feeScheduleState) ? feeScheduleState : feeSchedule,
      },
      NEWYORK: NEW_YORK_FEE_SCHEDULE,
    });
  },
);

const getShouldRenderGenericFeeSchedule = () => createSelector(
  selectRequestStatus('fees'),
  selectMainStatus('initializeRequestStatus'),
  (fetchFeesRequestStatus, initializeRequestStatus) =>
    fetchFeesRequestStatus === 'failed' || initializeRequestStatus === 'failed',
);

/**
 * Default selector used by Fees
 */
const makeSelectFees = (prop) => createSelector(
  selectFeesDomain,
  (feesState) => feesState[prop],
);

export default makeSelectFees;
export {
  getFormattedMobileStringFeeSchedule,
  getFormattedPreauthFees,
  getFormattedStringFeeSchedule,
  getShouldRenderGenericFeeSchedule,
  selectDesktopIsNewYorkFeeSchedule,
  selectDesktopPageTitle,
  selectHasPDFFeeStatement,
  selectRequestStatus,
  selectWhichDesktopFeeSchedule,
};
